import { Select } from 'antd';
import { FC, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  getSchedules,
  createSchedule,
  updateSchedule,
  deleteSchedule,
} from '../../../api/shuttleSetting.api';
import useAxios from '../../../hooks/useAxios';
import { IGetSchedules, ILine } from '../../../models/shuttleSetting.model';
import { ShuttleContents } from '../../../pages/campus/shuttle';
import { RootState } from '../../../store/rootReducer';
import ErrorResponse from '../../ErrorResponse';
import Loader from '../../Loader';
import Button from '../../../components/Button';
import moment from 'moment';
import SelectBusModal from './modals/SelectBusModal';
import SelectHelperModal from './modals/SelectHelperModal';
import useNotification from '../../../hooks/useNotification';
import ConfirmModal from '../../modals/ConfirmModal';
import { useHistory, useLocation } from 'react-router-dom';
import CustomTimePicker from '../../CustomTimePicker';
import QueryString from 'qs';
import useAuth from '../../../hooks/useAuth';
import CopyWeekdayModal from './modals/CopyWeekdayModal';
import TableEmpty from '../../common/TableEmpty';

interface IProps {
  line: ILine | undefined;
}

const ScheduleList: FC<IProps> = ({ line }) => {
  const { t } = useTranslation();
  const { writeAuthYn } = useAuth();
  const history = useHistory();
  const location = useLocation();
  const query = QueryString.parse(location.search, { ignoreQueryPrefix: true });
  const { infoNotification, successNotification } = useNotification();
  const { currentCampus } = useSelector((state: RootState) => state.auth);
  const { commonCodes } = useSelector((state: RootState) => state.commonCode);
  // SY0001 = 요일
  const SY0001 = commonCodes.find((item) => item.commonCode === 'SY0001')?.detailCode;
  const { data: res, isLoading, error, execute } = useAxios<IGetSchedules>();
  const { data: submitRes, isLoading: submitIsLoading, error: submitError, execute: submitExecute } = useAxios();
  const { data: deleteRes, isLoading: deleteIsLoading, error: deleteError,
    execute: deleteExecute } = useAxios();

  const [weekdayDiv, setWeekdayDiv] = useState<string>();
  const [schedules, setSchedules] = useState<any[]>([]);
  const [selectBusVisible, setSelectBusVisible] = useState(false);
  const [scheduleIndex, setScheduleIndex] = useState(-1);
  const [selectHelperVisible, setSelectHelperVisible] = useState(false);
  const [selectHelperMode, setSelectHelperMode] = useState('');
  const [copyWeekdayVisible, setCopyWeekdayVisible] = useState(false);
  const [confirmDeleteVisible, setConfirmDeleteVisible] = useState(false);
  const [runningCode, setRunningCode] = useState(-1);

  useEffect(() => {
    if (query.weekdayDiv) {
      setWeekdayDiv(String(query.weekdayDiv));
      doGetSchedules(String(query.weekdayDiv));
    }
  }, [location]);

  useEffect(() => {
    if (res && !isLoading) {
      setSchedules(res.data);
    }
  }, [res, isLoading]);

  useEffect(() => {
    if (submitRes && !submitIsLoading) {
      if (weekdayDiv) doGetSchedules();
      successNotification(t('common.message.save1'));
    }
  }, [submitRes, submitIsLoading]);

  useEffect(() => {
    if (deleteRes && !deleteIsLoading) {
      if (weekdayDiv) doGetSchedules();
      successNotification(t('common.message.delete'));
    }
  }, [deleteRes, deleteIsLoading]);

  const doGetSchedules = (weekday: string|undefined = weekdayDiv) => {
    if (!line || !weekday) return;
    execute(getSchedules(currentCampus, line.lineCode, weekday));
  };

  const onAddSchedule = () => {
    const _schedules = [...schedules];
    _schedules.push({
      runningCode: '',
      lineTime: '',
      busCode: '',
      busNo: '',
      attendMemCode: '',
      attendMemKorName: '',
      overMemCode: '',
      overMemKorName: '',
      seq: _schedules.length + 1,
    });
    setSchedules(_schedules);
  };

  const onSetSchedule = (index: number, key: string, value: string) => {
    const _schedules = [...schedules];
    _schedules[index][key] = value;
    setSchedules(_schedules);
  };

  const onDeleteSchedule = (index: number) => {
    if (schedules[index].passengers > 0) {
      return infoNotification(
        t('campus.shuttle.message11'),
      );
    }

    const _schedules = [...schedules];
    const [schedule] = _schedules.splice(index, 1);

    if (schedule.runningCode) {
      setRunningCode(schedule.runningCode);
      setConfirmDeleteVisible(true);
    } else {
      _schedules.forEach((item, i) => (item.seq = i + 1));
      setSchedules(_schedules);
    }
  };

  const doDeleteSchedule = () => {
    deleteExecute(deleteSchedule(currentCampus, runningCode));
    setConfirmDeleteVisible(false);
  };

  const onSubmit = (index: number) => {
    if (!line || !weekdayDiv) return;

    // 시간 중복 체크
    const times: any = [];
    schedules.forEach((item) => {
      times.push(item.lineTime);
    });
    if (new Set(times).size !== times.length) {
      return infoNotification(t('campus.shuttle.message12'));
    }

    const schedule = schedules[index];
    if (!schedule.lineTime) {
      return infoNotification(t('campus.shuttle.message13'));
    }
    if (!schedule.busCode) {
      return infoNotification(t('campus.shuttle.message14'));
    }
    if (!schedule.attendMemCode) {
      return infoNotification(t('campus.shuttle.message15'));
    }
    if (!schedule.overMemCode) {
      return infoNotification(t('campus.shuttle.message16'));
    }

    if (schedule.runningCode) {
      submitExecute(
        updateSchedule(currentCampus, line.lineCode, weekdayDiv, schedule),
      );
    } else {
      submitExecute(
        createSchedule(currentCampus, line.lineCode, {
          ...schedule,
          weekdayDiv,
        }),
      );
    }
  };

  return (
    <>
      {isLoading || submitIsLoading || deleteIsLoading ? <Loader /> : null}
      <ErrorResponse error={error} />
      <ErrorResponse error={submitError} />
      <ErrorResponse error={deleteError} />

      <div className='comm-bx__space'>
        <h3 className='sub-title'>{t('campus.shuttle.column17')} - {line?.lineName || ''}</h3>
      </div>
      <div className='comm-bx__between'>
        <div className='comm-btn__area'>
          <Button
            className="btn-basic pink-line"
            onClick={() => setCopyWeekdayVisible(true)}
            text={t('campus.shuttle.button3')}
          />
        </div>
        <div className="w-150p">
          <Select
            className="w-100"
            placeholder={t('campus.shuttle.placeholder4')}
            value={weekdayDiv}
            onChange={(value) => {
              history.push(`/campus/shuttle?index=6&weekdayDiv=${value}`);
            }}
          >
            {SY0001?.slice(1, 6).map((item: any) => {
              return (
                <Select.Option key={item.detailCode} value={item.detailCode}>
                  {item.korName}
                </Select.Option>
              );
            })}
          </Select>
        </div>
      </div>
      {weekdayDiv ? (
      <div className='comm-table__area rowType stats'>
        <table>
          <tbody>
            <tr>
              <th className="text-center">{t('campus.shuttle.column23')}</th>
              <th className="text-center">{t('campus.shuttle.column24')}</th>
              <th className="text-center">{t('campus.shuttle.column25')}</th>
              <th className="text-center">{t('campus.shuttle.column26')}</th>
              <th className="text-center">{t('common.button.save')}</th>
              <th className="text-center">{t('common.button.delete')}</th>
              <th className="text-center">{t('common.button.detail')}</th>
            </tr>
            {schedules.map((item: any, index: number) => (
              <tr key={index}>
                <td className="text-center">
                  <div className='time-picker__area'>
                    <CustomTimePicker
                      value={item.lineTime}
                      onSelect={(value) =>
                        onSetSchedule(
                          index,
                          'lineTime',
                          moment(value).format('HH:mm'),
                        )
                      }
                    />
                  </div>
                </td>
                <td>
                  <div className="comm-btn__area justify-content-center">
                    <Button
                      className="btn-basic small mint"
                      onClick={() => {
                        setScheduleIndex(index);
                        setSelectBusVisible(true);
                      }}
                      text={t('common.button.select')}
                    />
                    <span className="ml-2 v-line">
                      {item.busNo ? item.busNo : `(${t('campus.shuttle.text7')})`}
                    </span>
                  </div>
                </td>
                <td>
                  <div className="comm-btn__area justify-content-center">
                    <Button
                      className="btn-basic small mint"
                      onClick={() => {
                        setSelectHelperMode('attend');
                        setScheduleIndex(index);
                        setSelectHelperVisible(true);
                      }}
                      text={t('common.button.select')}
                    />
                    <span className="ml-2 v-line">
                      {item.attendMemKorName
                        ? item.attendMemKorName
                        : `(${t('campus.shuttle.text7')})`}
                    </span>
                  </div>
                </td>
                <td>
                  <div className="comm-btn__area justify-content-center">
                    <Button
                      className="btn-basic small mint"
                      onClick={() => {
                        setSelectHelperMode('over');
                        setScheduleIndex(index);
                        setSelectHelperVisible(true);
                      }}
                      text={t('common.button.select')}
                    />
                    <span className="ml-2 v-line">
                      {item.overMemKorName ? item.overMemKorName : `(${t('campus.shuttle.text7')})`}
                    </span>
                  </div>
                </td>
                <td className="text-center">
                  <div className="comm-btn__area justify-content-center">
                    <Button
                      className="btn-basic small pink"
                      onClick={() => onSubmit(index)}
                      text={t('common.button.save')}
                    />
                  </div>
                </td>
                <td className="text-center">
                  <div className="comm-btn__area justify-content-center">
                    <Button
                      className="btn-basic small gray"
                      onClick={() => onDeleteSchedule(index)}
                      text={t('common.button.delete')}
                      writeAuthYn={writeAuthYn}
                    />
                  </div>
                </td>
                <td className="text-center">
                  <div className="comm-btn__area justify-content-center">
                    <Button
                      className="btn-basic small mint"
                      onClick={() => {
                        history.push(
                          `/campus/shuttle?index=${ShuttleContents.ScheduleDetail}&weekdayDiv=${weekdayDiv}&scheduleCode=${item.runningCode}`,
                        );
                      }}
                      text={t('common.button.detail')}
                      disabled={!item.runningCode}
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
       ) : (
        <div className='comm-table__area'>
          <TableEmpty text={t('campus.shuttle.text8')} />
        </div>
      )}
      <div className='comm-bx__between'>
        <div className='comm-btn__area'>
          <Button
            className="btn-basic gray-line"
            onClick={() =>
              history.push(`/campus/shuttle?index=${ShuttleContents.LineList}`)
            }
            text={t('common.button.list')}
          />
        </div>
        {weekdayDiv && (
            <div className='comm-btn__area right'>
              <Button
                className="btn-basic pink-line"
                onClick={onAddSchedule}
                text={t('common.button.add')}
                writeAuthYn={writeAuthYn}
              />
            </div>
          )}
      </div>
      
      {selectBusVisible &&
        <SelectBusModal
          visible={selectBusVisible}
          setVisible={setSelectBusVisible}
          scheduleIndex={scheduleIndex}
          onSetSchedule={onSetSchedule}
        />
      }
      {selectHelperVisible &&
        <SelectHelperModal
          visible={selectHelperVisible}
          setVisible={setSelectHelperVisible}
          scheduleIndex={scheduleIndex}
          onSetSchedule={onSetSchedule}
          selectHelperMode={selectHelperMode}
        />
      }
      {copyWeekdayVisible &&
        <CopyWeekdayModal
          visible={copyWeekdayVisible}
          setVisible={setCopyWeekdayVisible}
          doGetSchedules={doGetSchedules}
          line={line}
        />
      }      
      {confirmDeleteVisible &&
        <ConfirmModal
          visible={confirmDeleteVisible}
          setVisible={setConfirmDeleteVisible}
          onConfirm={doDeleteSchedule}
          titleText={t('campus.shuttle.title9')}
          messageText={t('campus.shuttle.text9')}
          confirmText={t('common.button.delete')}
          cancelText={t('common.button.cancel')}
        />
      }
    </>
  );
};

export default ScheduleList;
